import { useEffect, type RefObject } from "react";

/** The CSS variable the page reads to leave room under a floating bar. */
export const FLOATING_BAR_VAR = "--mj-floating-bar-h";

/**
 * Publishes the height of a page's floating bottom bar (the booking bar on an
 * activity, spa or camp page, the hotel bar) as `--mj-floating-bar-h` on
 * <html>, for as long as the bar is on the page.
 *
 * The page pads its foot by that much below lg, so the last "Similar
 * listings" cards and their prices can scroll clear of the bar. The bar runs
 * 75-110px with the safe area and grows into a card after a booking, so a
 * fixed number either left the prices covered (it was 12px) or a gap under
 * pages with no bar at all (explore items). Measuring the bar itself gives
 * neither. The wrapper's own height already carries the safe-area padding.
 */
export function useFloatingBarRoom(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof document === "undefined") return;
    const root = document.documentElement;
    const publish = () => {
      // offsetHeight, not the bounding box: it ignores the scale() the bar
      // shrinks with while the guest scrolls, so the room does not jump.
      // Zero when the bar is display:none (lg and up), which is the right room.
      root.style.setProperty(FLOATING_BAR_VAR, `${el.offsetHeight}px`);
    };
    publish();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    return () => {
      ro?.disconnect();
      root.style.removeProperty(FLOATING_BAR_VAR);
    };
  }, [ref]);
}
